"use client";

import React, { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Menu, Search, User, ChevronDown, ArrowRight } from "lucide-react";

// Recursive dropdown items renderer for desktop navigation submenus
function SubmenuDropdown({
  items
}: {
  items: MenuItem[];
}) {
  if (!items || items.length === 0) return null;
  return <ul className="min-w-[200px] p-2 space-y-1 bg-popover text-popover-foreground rounded-md shadow-md border border-border">
      {items.map((item, i) => <li key={item.id} data-index={i} className="relative group/sub">
          {item.children && item.children.length > 0 ? <div className="space-y-1">
              <Link to={item.href || "#"} className="flex items-center justify-between px-3 py-2 text-sm rounded-sm hover:bg-muted text-foreground transition-colors">
                <span>{item.label}</span>
                <ChevronDown className="size-3.5 -rotate-90 text-muted-foreground" />
              </Link>
              <div className="pl-3 border-l border-border mt-1">
                <SubmenuDropdown items={item.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="block px-3 py-2 text-sm rounded-sm hover:bg-muted text-foreground transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}

// Desktop Menu Consumer inside MenuProvider
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-3">
        <div className="h-8 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1 sm:gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="h-9 px-3.5 text-sm font-medium text-foreground bg-transparent hover:bg-muted hover:text-foreground">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="p-0 border-0 bg-transparent shadow-none">
                  <SubmenuDropdown items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="h-9 px-3.5 inline-flex items-center text-sm font-medium text-foreground rounded-md hover:bg-muted hover:text-primary transition-colors">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive list renderer for mobile drawer menu
function MobileMenuList({
  items,
  onNavigate
}: {
  items: MenuItem[];
  onNavigate: () => void;
}) {
  if (!items || items.length === 0) return null;
  return <ul className="space-y-1">
      {items.map((item, i) => <li key={item.id} data-index={i} className="space-y-1">
          <Link to={item.href || "#"} onClick={onNavigate} className="flex items-center justify-between px-3 py-2 text-base font-medium rounded-md text-foreground hover:bg-muted transition-colors">
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <div className="pl-4 border-l border-border ml-2 space-y-1">
              <MobileMenuList items={item.children} onNavigate={onNavigate} />
            </div>}
        </li>)}
    </ul>;
}

// Mobile Menu Consumer inside MenuProvider
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 p-4">
        <div className="h-9 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-1/2 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-2/3 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="py-2">
      <MobileMenuList items={menuItems} onNavigate={onClose} />
    </div>;
}
export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const accountUrl = useWooAccountUrl();

  // Smooth scroll handler for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 80;
            const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/shop?s=${encodeURIComponent(searchQuery.trim())}`;
      setSearchOpen(false);
    }
  };
  return <section id="header" data-nav="light" className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur-md border-b border-border transition-colors duration-200">
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
        {/* Columns 1–3: Brand Logo */}
        <div className="flex items-center shrink-0">
          <Link to="/" className="inline-flex items-center">
            <WvcLogo className="h-12 w-auto" />
          </Link>
        </div>

        {/* Columns 4–9: Center Desktop Navigation */}
        <nav className="hidden lg:flex flex-1 justify-center items-center px-4" aria-label="Main Navigation">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </nav>

        {/* Columns 10–12: Utilities (Search, Account, Cart, CTA) + Mobile Toggle */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Quick Search Popover */}
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Search equipment catalog" className="text-foreground hover:bg-muted">
                <Search className="size-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-3 bg-card border-border shadow-md">
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                <input type="text" placeholder="Model, fuel type, BTU..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 h-9 px-3 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" autoFocus />
                <Button type="submit" size="sm" variant="default" className="bg-primary text-primary-foreground">
                  <ArrowRight className="size-4" />
                </Button>
              </form>
            </PopoverContent>
          </Popover>

          {/* Account Icon */}
          <Button asChild variant="ghost" size="icon" aria-label="Customer account" className="text-foreground hover:bg-muted">
            <Link to={accountUrl}>
              <User className="size-5" />
            </Link>
          </Button>

          {/* Cart Icon Drawer Trigger */}
          <CartIcon variant="ghost" size="icon" iconSize="size-5" cartLayout="drawer" cartItemCountDisplay="only-if-items" />

          {/* Primary Action Button: "Shop Now" */}
          <div className="hidden sm:inline-flex">
            <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/shop">Shop Now</Link>
            </Button>
          </div>

          {/* Mobile Menu Hamburger Sheet Trigger */}
          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open mobile navigation menu" className="text-foreground hover:bg-muted">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] p-6 flex flex-col justify-between">
                <div>
                  <SheetHeader className="p-0 pb-4 border-b border-border text-left">
                    <SheetTitle className="text-lg font-bold text-foreground">Menu</SheetTitle>
                  </SheetHeader>

                  <div className="mt-4">
                    <MenuProvider menu_id="16">
                      <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                    </MenuProvider>
                  </div>
                </div>

                <div className="pt-6 border-t border-border flex flex-col gap-3">
                  <Button asChild className="w-full bg-primary text-primary-foreground">
                    <Link to="/shop" onClick={() => setMobileOpen(false)}>
                      Shop Now
                    </Link>
                  </Button>
                  <div className="flex items-center justify-around pt-2 text-sm text-muted-foreground">
                    <Link to={accountUrl} onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-1.5 hover:text-foreground">
                      <User className="size-4" />
                      <span>Account</span>
                    </Link>
                    <div className="inline-flex items-center gap-1.5">
                      <CartIcon variant="ghost" size="sm" iconSize="size-4" cartLayout="drawer" cartItemCountDisplay="only-if-items" />
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}